import { Badge, type BadgeTone, type IconName } from "@/components";
import { Decimal } from "@/lib/decimal";
import { formatNumber } from "@/lib/format";
import type { FormulaKind, LineFlag, LineUnit, MaterialBrief, VersionSource, VersionStatus } from "./api";

export const UNIT_LABELS: Record<LineUnit, string> = { pct: "%", parts: "parts", g: "g" };

export const UNIT_OPTIONS: { value: LineUnit; label: string }[] = [
  { value: "pct", label: "%" },
  { value: "parts", label: "parts" },
  { value: "g", label: "g" },
];

export const KIND_LABELS: Record<FormulaKind, string> = { formula: "Formula", accord: "Accord", dilution: "Dilution" };

export const SOURCE_LABELS: Record<VersionSource, string> = {
  manual: "Written here",
  paste: "Pasted",
  import: "Imported",
  trial_addition: "From trial additions",
  ai: "Assistant proposal",
  ladder: "From a ladder",
  copy: "Copy",
};

export const FLAG_LOOK: Record<Exclude<LineFlag, "none">, { label: string; tone: BadgeTone; icon: IconName }> = {
  too_strong: { label: "Too strong", tone: "warning", icon: "arrowUp" },
  too_weak: { label: "Too weak", tone: "info", icon: "arrowDown" },
  highlight: { label: "Highlight", tone: "neutral", icon: "tag" },
};

const STATUS_LOOK: Record<VersionStatus, { label: string; tone: BadgeTone; icon: IconName }> = {
  draft: { label: "Draft", tone: "neutral", icon: "pencil" },
  released: { label: "Released", tone: "info", icon: "check" },
  locked: { label: "Locked", tone: "success", icon: "shield" },
};

export function VersionStatusBadge({ status }: { status: VersionStatus }) {
  const look = STATUS_LOOK[status];
  return (
    <Badge tone={look.tone} icon={look.icon}>
      {look.label}
    </Badge>
  );
}

/** A 0–1 fraction as a plain percent string for inputs: "0.1" -> "10". */
export function fractionToPercentInput(fraction: string | null | undefined): string | null {
  const value = Decimal.tryFrom(fraction);
  return value === null ? null : value.shift(2).toString();
}

/** A percent input back to a fraction string: "10" -> "0.1". */
export function percentInputToFraction(percent: string | null | undefined): string | null {
  const value = Decimal.tryFrom(percent);
  return value === null ? null : value.shift(-2).toString();
}

/** "10 % in Dipropylene glycol", "Neat" or "10 % dilution". */
export function dilutionText(activeFraction: string, carrier: MaterialBrief | null | undefined): string {
  const value = Decimal.tryFrom(activeFraction);
  if (value === null || value.compare(Decimal.ONE) >= 0) return "Neat";
  const percent = formatNumber(value.shift(2), { maxDecimals: 3 });
  return carrier ? `${percent}\u00a0% in ${carrier.name}` : `${percent}\u00a0% dilution`;
}

export function materialLabel(material: MaterialBrief | null | undefined): string {
  if (!material) return "—";
  return material.short_code ? `${material.name} (${material.short_code})` : material.name;
}
